import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { AuthService } from '../../services/auth.service';
import { MatListModule } from '@angular/material/list';

// 🆕 NUEVA INTERFAZ PARA SUGERENCIAS
interface RoomSuggestion {
  rooms: any[];
  totalCapacity: number;
  totalPrice: number;
  isValid: boolean;
  message: string;
}

@Component({
  selector: 'app-reservation-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatChipsModule,
    MatListModule
  ],
  templateUrl: './reservation-modal.component.html',
  styleUrls: ['./reservation-modal.component.css']
})
export class ReservationModalComponent implements OnInit {
  // Formularios
  reservationForm: FormGroup;
  clientSearchForm: FormGroup;
  clientCreateForm: FormGroup;
  guestForm: FormGroup;
  searchAvailabilityForm: FormGroup;
  verificationForm: FormGroup;
  // Datos
  client: any | null = null;
  clientPendingVerification: any | null = null;
  showVerificationStep = false;
  verificationError = '';
  guests: any[] = [];
  extraServices: any[] = [];
  accommodations: any[] = [];
  selectedAccommodation: any | null = null;
  availableRooms: any[] = [];
  paymentMethods: any[] = [];

  // 🆕 NUEVAS PROPIEDADES PARA SUGERENCIAS
  roomSuggestions: RoomSuggestion[] = [];
  selectedRooms: any[] = []; // Múltiples habitaciones seleccionadas
  showSuggestions = false;
  noRoomSuggestions = false; // Para mostrar mensaje cuando no hay sugerencias válidas

  // Estados
  showCreateClient = false;
  clientError = '';
  isEditMode = false;

  // Opciones
  ID_TYPES = [
    'Cédula de Ciudadanía',
    'Cédula de Extranjería',
    'Pasaporte',
    'Tarjeta de Identidad'
  ];

  RESERVATION_STATUSES = [
    { value: 'Pendiente', label: 'Pendiente' },
    { value: 'Confirmada', label: 'Confirmada' },
    { value: 'Cancelada', label: 'Cancelada' },
    { value: 'Completada', label: 'Completada' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ReservationModalComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: { reservation?: any; accommodations: any[] }
  ) {
    this.accommodations = data.accommodations || [];
    this.isEditMode = !!data.reservation;

    // 🔍 FORMULARIO DE BÚSQUEDA DE DISPONIBILIDAD
    this.searchAvailabilityForm = this.fb.group({
      check_in: ['', Validators.required],
      check_out: ['', Validators.required],
      adults: [1, [Validators.required, Validators.min(1)]],
      children: [0, [Validators.min(0)]],
      rooms: [1, [Validators.required, Validators.min(1)]]
    });

    this.verificationForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6)]]
    });

    // 📌 FORMULARIO PRINCIPAL DE RESERVA
    this.reservationForm = this.fb.group({
      accommodation_id: ['', Validators.required],
      room_id: [{ value: '', disabled: true }],
      status: ['Pendiente'] // fijo
    });


    // 👤 BÚSQUEDA DE CLIENTE
    this.clientSearchForm = this.fb.group({
      email: [''],
      document_number: ['']
    });

    // ➕ CREACIÓN DE CLIENTE
    this.clientCreateForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      given_name: ['', Validators.required],
      family_name: ['', Validators.required],
      document_type: ['Cédula de Ciudadanía', Validators.required],
      document_number: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });

    // 👥 HUÉSPEDES
    this.guestForm = this.fb.group({
      given_name: ['', Validators.required],
      family_name: ['', Validators.required],
      document_type: ['Cédula de Ciudadanía', Validators.required],
      document_number: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]]
    });
  }

  get accommodationCtrl() {
    return this.reservationForm.get('accommodation_id') as any;
  }

  get paymentMethodCtrl() {
    return this.reservationForm.get('payment_method') as any;
  }

  ngOnInit(): void {
    if (this.isEditMode && this.data.reservation) {
      this.loadReservationData(this.data.reservation);
    }

    this.reservationForm.get('accommodation_id')?.valueChanges.subscribe(accommodationId => {
      this.onAccommodationChange(accommodationId);
    });

    this.reservationForm.get('room_id')?.valueChanges.subscribe(roomId => {
      this.onRoomChange(roomId);
    });
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  loadReservationData(reservation: any): void {
    if (reservation.client) {
      this.client = reservation.client;
    }

    this.reservationForm.patchValue({
      accommodation_id: reservation.accommodation?.id || '',
      room_id: reservation.room?.id || '',
      check_in: this.formatDateForInput(reservation.check_in),
      check_out: this.formatDateForInput(reservation.check_out),
      status: reservation.status || 'Pendiente',
      payment_method: reservation.payment?.payment_method_id || ''
    });

    if (reservation.guests_detail && Array.isArray(reservation.guests_detail)) {
      this.guests = reservation.guests_detail.map((guest: any) => ({
        id: guest.id || '',
        given_name: guest.given_name || guest.givenName,
        family_name: guest.family_name || guest.familyName,
        document_type: guest.document_type,
        document_number: guest.document_number,
        phone: guest.phone,
        email: guest.email
      }));
    }

    setTimeout(() => {
      this.onAccommodationChange(reservation.accommodation?.id);
    });
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  private getServiceName(serviceId: string): string {
    for (const accommodation of this.accommodations) {
      if (accommodation.extra_services) {
        const service = accommodation.extra_services.find((s: any) => s.id === serviceId);
        if (service) return service.name;
      }
    }
    return 'Servicio';
  }

 handleSearchClient(): void {
  this.clientError = '';

  const email = this.clientSearchForm.get('email')?.value;
  const document_number = this.clientSearchForm.get('document_number')?.value;

  if (!email && !document_number) {
    this.clientError = 'Ingrese el email o documento del cliente';
    return;
  }

  this.authService.findUserByEmailOrDocument(email || document_number)
    .then((response: any) => {
      if (response?.user) {
        this.client = response.user;
        this.showCreateClient = false;
        this.showVerificationStep = false;
        return;
      }

      this.clientError = 'Cliente no encontrado';
    })
    .catch(() => {
      this.clientError = 'Error al buscar el cliente';
    });
}



 handleCreateClient(): void {
  this.clientError = '';
  this.verificationError = '';

  if (this.clientCreateForm.invalid) {
    this.clientError = 'Por favor complete todos los campos correctamente';
    return;
  }

  if (this.clientCreateForm.hasError('passwordMismatch')) {
    this.clientError = 'Las contraseñas no coinciden';
    return;
  }

  const formValue = this.clientCreateForm.value;

  this.authService.registerUser(
    formValue.email,
    formValue.password,
    formValue.given_name,
    formValue.family_name,
    formValue.document_number,
    'user'
  ).then(() => {
    // 🆕 Usuario NUEVO → sí requiere verificación
    this.askForVerification(formValue);
  }).catch((error: any) => {

    // 👤 Usuario ya existe
    if (error?.code === 'UsernameExistsException') {

      // 👉 Intentar login
      this.authService.signIn(formValue.email, formValue.password)
        .then(() => {
          // ✅ Usuario EXISTE y está CONFIRMADO
          this.client = {
            email: formValue.email,
            given_name: formValue.given_name,
            family_name: formValue.family_name,
            document_number: formValue.document_number
          };

          this.showVerificationStep = false;
          this.clientCreateForm.reset();
        })
        .catch((loginError: any) => {

          // 🔐 Existe pero NO confirmado
          if (loginError?.code === 'UserNotConfirmedException') {
            this.askForVerification(formValue);
            return;
          }

          this.clientError = 'Credenciales incorrectas';
        });

      return;
    }

    this.clientError = 'Error al crear usuario';
  });
}

private askForVerification(formValue: any): void {
  this.clientPendingVerification = {
    email: formValue.email,
    given_name: formValue.given_name,
    family_name: formValue.family_name,
    document_number: formValue.document_number
  };

  this.showVerificationStep = true;
  this.showCreateClient = false;
}

  removeClient(): void {
    this.client = null;
    this.clientSearchForm.reset();
  }

  onAccommodationChange(accommodationId: string): void {
    this.selectedAccommodation = this.accommodations.find(a => a.id === accommodationId) || null;

    this.reservationForm.get('room_id')?.reset();
    this.reservationForm.get('room_id')?.disable();
    this.availableRooms = [];
    this.selectedRooms = [];
    this.showSuggestions = false;

    if (!this.selectedAccommodation) {
      this.extraServices = [];
      this.paymentMethods = [];
      return;
    }

    this.availableRooms = this.selectedAccommodation.rooms || [];

    const accommodationServices = (this.selectedAccommodation.extra_services || []).map((service: any) => ({
      ...service,
      quantity: 0,
      cost: parseFloat(service.cost) || 0
    }));

    if (this.isEditMode && this.data.reservation?.services) {
      this.extraServices = this.restoreServicesWithQuantities(accommodationServices);
    } else {
      this.extraServices = accommodationServices;
    }

    this.paymentMethods = this.selectedAccommodation.payment_methods || [];
  }

  private restoreServicesWithQuantities(accommodationServices: any[]): any[] {
    if (!this.data.reservation?.services) return accommodationServices;

    const reservationServicesMap = new Map();
    this.data.reservation.services.forEach((service: any) => {
      reservationServicesMap.set(service.service_id, service);
    });

    return accommodationServices.map(service => {
      const reservationService = reservationServicesMap.get(service.id);
      if (reservationService) {
        return {
          ...service,
          quantity: reservationService.quantity || 0,
          cost: parseFloat(reservationService.unit_price) || service.cost
        };
      }
      return service;
    });
  }

  onRoomChange(roomId: string): void {
    const selectedRoom = this.availableRooms.find(r => r.id === roomId);
    if (selectedRoom) {
      // La habitación está seleccionada
    }
  }

  getSelectedRoom(): any | null {
    const roomId = this.reservationForm.get('room_id')?.value;
    return this.availableRooms.find(r => r.id === roomId) || null;
  }

  // 🆕 MÉTODO PRINCIPAL PARA BUSCAR DISPONIBILIDAD Y GENERAR SUGERENCIAS
searchAvailability(): void {
  const totalPeople = this.getTotalPeople();
  const requestedRooms = Number(this.searchAvailabilityForm.get('rooms')?.value || 1);

  if (!this.selectedAccommodation) {
    alert('Por favor seleccione un alojamiento primero');
    return;
  }

  let availableRooms = this.selectedAccommodation.rooms || [];

  if (availableRooms.length === 0) {
    alert('No hay habitaciones disponibles en este alojamiento');
    return;
  }

  console.log('📦 Habitaciones crudas:', availableRooms);

  // 🔥 NORMALIZACIÓN ÚNICA Y CORRECTA
  availableRooms = availableRooms.map((room: any) => {
    if (!room.capacity || Number(room.capacity) <= 0) {
      console.warn('⚠️ Habitación sin capacidad válida:', room);
    }

    return {
      ...room,
      name: room.type
        ? `${room.type} - Habitación ${room.number}`
        : `Habitación ${room.number}`,
      capacity: Number(room.capacity) || 0,
      price_per_night: Number(room.price_per_night) || 0
    };
  });

  console.log('✅ Habitaciones normalizadas:', availableRooms);
  console.log(`👥 Personas: ${totalPeople}, 🛏 Habitaciones solicitadas: ${requestedRooms}`);

  // 🧠 GENERAR SUGERENCIAS
  this.roomSuggestions = this.generateRoomSuggestions(
    availableRooms,
    totalPeople,
    requestedRooms
  );

  // ¿Hay alguna sugerencia válida?
  this.noRoomSuggestions = this.roomSuggestions.length > 0
    ? this.roomSuggestions.every(s => !s.isValid)
    : true;

  this.showSuggestions = true;

  // 🔒 Habilitar / deshabilitar room_id según selección
  if (requestedRooms === 1) {
    this.reservationForm.get('room_id')?.enable();
  } else {
    this.reservationForm.get('room_id')?.disable();
  }
}

  // 🆕 GENERA TODAS LAS COMBINACIONES POSIBLES DE HABITACIONES
// 🔧 SOLUCIÓN: Reemplaza el método generateRoomSuggestions con esta versión mejorada

// 🔧 SOLUCIÓN: Reemplaza el método generateRoomSuggestions con esta versión mejorada

private generateRoomSuggestions(
  rooms: any[],
  totalPeople: number,
  requestedRooms: number
): RoomSuggestion[] {
  const suggestions: RoomSuggestion[] = [];


  // CASO 1: Buscar habitación individual que acomode a todos
  if (requestedRooms === 1) {
    const singleRoomOptions = rooms.filter(room => room.capacity >= totalPeople);

    singleRoomOptions.forEach(room => {
      suggestions.push({
        rooms: [room],
        totalCapacity: room.capacity,
        totalPrice: room.price_per_night,
        isValid: true,
        message: `1 habitación "${room.name}" con capacidad para ${room.capacity} personas`
      });
    });

    // Si no hay habitación individual suficiente, sugerir múltiples habitaciones
    if (singleRoomOptions.length === 0) {
      console.log('⚠️ No hay habitación individual suficiente. Sugiriendo combinaciones automáticamente...');

      const capacities = rooms.map(r => r.capacity).filter(c => c > 0);

      if (capacities.length === 0) {
        console.error('❌ ERROR: Ninguna habitación tiene capacidad definida!');
        rooms.forEach(room => {
          suggestions.push({
            rooms: [room],
            totalCapacity: 0,
            totalPrice: room.price_per_night,
            isValid: false,
            message: `ERROR: Habitación "${room.name}" no tiene capacidad definida. Verifique los datos.`
          });
        });
        return suggestions;
      }

      const maxCapacityPerRoom = Math.max(...capacities);
      const roomsNeeded = Math.ceil(totalPeople / maxCapacityPerRoom);

      for (let numRooms = roomsNeeded; numRooms <= Math.min(rooms.length, roomsNeeded + 2); numRooms++) {
        const combinations = this.findRoomCombinations(rooms, totalPeople, numRooms);

        combinations.forEach(combo => {
          const totalCapacity = combo.reduce((sum, room) => sum + room.capacity, 0);
          const totalPrice = combo.reduce((sum, room) => sum + room.price_per_night, 0);
          const roomNames = combo.map(r => r.name).join(' + ');

          suggestions.push({
            rooms: combo,
            totalCapacity: totalCapacity,
            totalPrice: totalPrice,
            isValid: true,
            message: `${combo.length} habitaciones: ${roomNames} (capacidad total: ${totalCapacity} personas)`
          });
        });
      }

      if (suggestions.length === 0) {
        rooms.forEach(room => {
          suggestions.push({
            rooms: [room],
            totalCapacity: room.capacity,
            totalPrice: room.price_per_night,
            isValid: false,
            message: `1 habitación "${room.name}" (capacidad: ${room.capacity} personas - insuficiente para ${totalPeople}). Se recomiendan ${roomsNeeded} habitaciones.`
          });
        });
      }
    }
  }

  // CASO 2: Múltiples habitaciones solicitadas
  if (requestedRooms > 1) {
    const combinations = this.findRoomCombinations(rooms, totalPeople, requestedRooms);

    combinations.forEach(combo => {
      const totalCapacity = combo.reduce((sum, room) => sum + room.capacity, 0);
      const totalPrice = combo.reduce((sum, room) => sum + room.price_per_night, 0);
      const roomNames = combo.map(r => r.name).join(' + ');

      suggestions.push({
        rooms: combo,
        totalCapacity: totalCapacity,
        totalPrice: totalPrice,
        isValid: totalCapacity >= totalPeople,
        message: `${combo.length} habitaciones: ${roomNames} (capacidad total: ${totalCapacity} personas)`
      });
    });

    if (combinations.length === 0) {
      const allCombinations = this.findAllRoomCombinations(rooms, requestedRooms);

      allCombinations.forEach(combo => {
        const totalCapacity = combo.reduce((sum, room) => sum + room.capacity, 0);
        const totalPrice = combo.reduce((sum, room) => sum + room.price_per_night, 0);
        const roomNames = combo.map(r => r.name).join(' + ');
        const isValid = totalCapacity >= totalPeople;

        suggestions.push({
          rooms: combo,
          totalCapacity: totalCapacity,
          totalPrice: totalPrice,
          isValid: isValid,
          message: isValid
            ? `${combo.length} habitaciones: ${roomNames} (capacidad total: ${totalCapacity} personas)`
            : `${combo.length} habitaciones: ${roomNames} (capacidad: ${totalCapacity} - insuficiente para ${totalPeople})`
        });
      });
    }
  }

  // 🔥 ELIMINAR DUPLICADOS: Agrupar por capacidad + precio
  const uniqueSuggestions = this.removeDuplicateSuggestions(suggestions);

  // Ordenar sugerencias: válidas primero, luego por precio
  const sortedSuggestions = uniqueSuggestions.sort((a, b) => {
    if (a.isValid && !b.isValid) return -1;
    if (!a.isValid && b.isValid) return 1;
    return a.totalPrice - b.totalPrice;
  });

  return sortedSuggestions;
}

// 🆕 NUEVO MÉTODO: Elimina sugerencias duplicadas basadas en capacidad y precio
private removeDuplicateSuggestions(suggestions: RoomSuggestion[]): RoomSuggestion[] {
  const uniqueMap = new Map<string, RoomSuggestion>();

  suggestions.forEach(suggestion => {
    // Crear una clave única basada en:
    // - Número de habitaciones
    // - Capacidad total
    // - Precio total
    // - Estado de validez
    const key = `${suggestion.rooms.length}_${suggestion.totalCapacity}_${suggestion.totalPrice}_${suggestion.isValid}`;

    // Si ya existe una sugerencia con esta clave, mantener la primera
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, suggestion);
    } else {
      // Si ya existe, verificar si esta combinación es "mejor" (ej. mejor distribución)
      const existing = uniqueMap.get(key)!;

      // Preferir combinaciones con habitaciones de números más bajos (más organizadas)
      const currentAvgNumber = suggestion.rooms.reduce((sum, r) => sum + (r.number || 0), 0) / suggestion.rooms.length;
      const existingAvgNumber = existing.rooms.reduce((sum, r) => sum + (r.number || 0), 0) / existing.rooms.length;

      if (currentAvgNumber < existingAvgNumber) {
        uniqueMap.set(key, suggestion);
      }
    }
  });

  return Array.from(uniqueMap.values());
}

// Método removido - los mensajes se generan directamente en generateRoomSuggestions

  // 🆕 ENCUENTRA COMBINACIONES DE HABITACIONES QUE CUMPLAN CAPACIDAD
  private findRoomCombinations(rooms: any[], totalPeople: number, numRooms: number): any[][] {
    const combinations: any[][] = [];

    // Generar combinaciones recursivamente
    const generateCombos = (current: any[], start: number) => {
      if (current.length === numRooms) {
        const totalCapacity = current.reduce((sum, room) => sum + room.capacity, 0);
        if (totalCapacity >= totalPeople) {
          combinations.push([...current]);
        }
        return;
      }

      for (let i = start; i < rooms.length; i++) {
        current.push(rooms[i]);
        generateCombos(current, i + 1);
        current.pop();
      }
    };

    generateCombos([], 0);

    // Limitar a las 10 mejores combinaciones por precio
    return combinations
      .sort((a, b) => {
        const priceA = a.reduce((sum, room) => sum + room.price_per_night, 0);
        const priceB = b.reduce((sum, room) => sum + room.price_per_night, 0);
        return priceA - priceB;
      })
      .slice(0, 10);
  }

  // 🆕 ENCUENTRA TODAS LAS COMBINACIONES POSIBLES (sin filtro de capacidad)
  private findAllRoomCombinations(rooms: any[], numRooms: number): any[][] {
    const combinations: any[][] = [];

    // Generar TODAS las combinaciones sin importar capacidad
    const generateCombos = (current: any[], start: number) => {
      if (current.length === numRooms) {
        combinations.push([...current]);
        return;
      }

      for (let i = start; i < rooms.length; i++) {
        current.push(rooms[i]);
        generateCombos(current, i + 1);
        current.pop();
      }
    };

    generateCombos([], 0);

    // Ordenar por precio y limitar
    return combinations
      .sort((a, b) => {
        const priceA = a.reduce((sum, room) => sum + room.price_per_night, 0);
        const priceB = b.reduce((sum, room) => sum + room.price_per_night, 0);
        return priceA - priceB;
      })
      .slice(0, 10);
  }

  // 🆕 SELECCIONA UNA SUGERENCIA
  selectSuggestion(suggestion: RoomSuggestion): void {
    if (!suggestion.isValid) {
      alert('Esta combinación no tiene capacidad suficiente');
      return;
    }

    this.selectedRooms = suggestion.rooms;

    // Si es una sola habitación, usar el campo room_id normal
    if (suggestion.rooms.length === 1) {
      this.reservationForm.patchValue({
        room_id: suggestion.rooms[0].id
      });
    } else {
      // Para múltiples habitaciones, limpiar room_id
      this.reservationForm.patchValue({
        room_id: null
      });
    }
  }

  // 🆕 VERIFICA SI UNA SUGERENCIA ESTÁ SELECCIONADA
  isSuggestionSelected(suggestion: RoomSuggestion): boolean {
    if (this.selectedRooms.length === 0) return false;

    if (suggestion.rooms.length !== this.selectedRooms.length) return false;

    return suggestion.rooms.every(room =>
      this.selectedRooms.some(selected => selected.id === room.id)
    );
  }

  incrementService(serviceId: string): void {
    const service = this.extraServices.find(s => s.id === serviceId);
    if (service) {
      service.quantity++;
    }
  }

  decrementService(serviceId: string): void {
    const service = this.extraServices.find(s => s.id === serviceId);
    if (service && service.quantity > 0) {
      service.quantity--;
    }
  }

  getServiceTotal(service: any): number {
    return service.cost * service.quantity;
  }

  addGuest(): void {
    if (this.guestForm.invalid) {
      this.guestForm.markAllAsTouched();
      return;
    }

    const newGuest: any = this.guestForm.value;
    this.guests.push(newGuest);

    this.guestForm.reset({
      idType: 'Cédula de Ciudadanía'
    });
  }

  removeGuest(index: number): void {
    this.guests.splice(index, 1);
  }

  calculateNights(): number {
    const checkIn = this.searchAvailabilityForm.get('check_in')?.value;
    const checkOut = this.searchAvailabilityForm.get('check_out')?.value;

    if (!checkIn || !checkOut) return 0;

    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diff = checkOutDate.getTime() - checkInDate.getTime();
    const nights = Math.ceil(diff / (1000 * 3600 * 24));

    return nights > 0 ? nights : 0;
  }

  // 🆕 CALCULA EL TOTAL DE HABITACIONES (una o múltiples)
  calculateRoomTotal(): number {
    const nights = this.calculateNights();

    if (this.selectedRooms.length > 0) {
      // Múltiples habitaciones
      const totalPricePerNight = this.selectedRooms.reduce(
        (sum, room) => sum + room.price_per_night,
        0
      );
      return totalPricePerNight * nights;
    } else {
      // Una sola habitación
      const selectedRoom = this.getSelectedRoom();
      if (!selectedRoom) return 0;
      return selectedRoom.price_per_night * nights;
    }
  }

  // Getter para obtener el total de habitaciones seleccionadas por noche
  get selectedRoomsTotal(): number {
    if (this.selectedRooms.length > 0) {
      return this.selectedRooms.reduce((sum, room) => sum + room.price_per_night, 0);
    }
    return 0;
  }

  calculateServicesTotal(): number {
    return this.extraServices.reduce((sum, service) => {
      return sum + (service.cost * service.quantity);
    }, 0);
  }

  calculateSubtotal(): number {
    return this.calculateRoomTotal() + this.calculateServicesTotal();
  }

  calculateTotal(): number {
    return this.calculateSubtotal();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // 🆕 VALIDACIÓN MEJORADA ANTES DE GUARDAR
  handleSubmit(): void {
    if (!this.client) {
      this.clientError = 'Debe buscar o crear un cliente antes de continuar';
      return;
    }

    if (this.reservationForm.invalid) {
      this.reservationForm.markAllAsTouched();
      return;
    }

    // Validar que se haya seleccionado al menos una habitación
    if (this.selectedRooms.length === 0 && !this.reservationForm.get('room_id')?.value) {
      alert('Debe seleccionar al menos una habitación');
      return;
    }

    const checkIn = this.searchAvailabilityForm.get('check_in')?.value;
    const checkOut = this.searchAvailabilityForm.get('check_out')?.value;
    const userId = this.client?.id || this.authService.getUser();

    const reservationData = {
      ...this.reservationForm.value,
      check_in: checkIn,
      check_out: checkOut,
      client: this.client,
      selectedRooms: this.selectedRooms.length > 0 ? this.selectedRooms : [this.getSelectedRoom()],
      extra_services: this.extraServices
        .filter(s => s.quantity > 0)
        .map(service => ({
          id: service.id,
          quantity: service.quantity,
          cost: service.cost
        })),
      payment: {
        id: this.data.reservation?.payment?.id || '',
        subtotal_value: this.calculateSubtotal(),
        total_value: this.calculateTotal()
      },
      subtotal_value: this.calculateSubtotal(),
      total_value: this.calculateTotal(),
      user_id: userId
    };

    this.dialogRef.close(reservationData);
  }

  getTotalPeople(): number {
    const adults = this.searchAvailabilityForm.get('adults')?.value || 0;
    const children = this.searchAvailabilityForm.get('children')?.value || 0;
    return adults + children;
  }

  // 🆕 GENERA SUGERENCIA INTELIGENTE
  getSmartSuggestion(): string | null {
    if (!this.selectedAccommodation?.rooms || this.roomSuggestions.length === 0) {
      return null;
    }

    const totalPeople = this.getTotalPeople();
    const requestedRooms = this.searchAvailabilityForm.get('rooms')?.value || 1;
    const availableRooms = this.selectedAccommodation.rooms;

    // Calcular cuántas habitaciones se necesitan
      const capacities = availableRooms
    .map((r: any) => Number(r.capacity) || 0)
    .filter((c: number) => c > 0);


    if (capacities.length === 0) return null;

    const maxCapacity = Math.max(...capacities);
    const roomsNeeded = Math.ceil(totalPeople / maxCapacity);

    if (roomsNeeded > requestedRooms) {
      return `💡 Sugerencia: Para ${totalPeople} personas, se necesitan al menos ${roomsNeeded} habitaciones. Ajusta tu búsqueda a ${roomsNeeded} habitaciones.`;
    }

    if (roomsNeeded < requestedRooms) {
      return `💡 Con ${requestedRooms} habitaciones puedes alojar hasta ${requestedRooms * maxCapacity} personas.`;
    }

    return null;
  }

  get roomIdControl(): FormControl {
    return this.reservationForm.get('room_id') as FormControl;
  }

  confirmVerificationCode(): void {
  if (this.verificationForm.invalid || !this.clientPendingVerification) {
    return;
  }

  const code = this.verificationForm.get('code')?.value;
  const email = this.clientPendingVerification.email;

  this.authService.confirmSignUp(email, code).then(() => {
    // ✅ Email confirmado
    this.client = this.clientPendingVerification;
    this.clientPendingVerification = null;
    this.showVerificationStep = false;
    this.verificationForm.reset();
  }).catch(() => {
    this.verificationError = 'Código inválido o expirado';
  });
}

  onCancel(): void {
    this.dialogRef.close();
  }
}
